import React, { useState } from "react";
import ExplanationBlock from "./ExplanationBlock";

const HEADER_CONFIG = [
  {
    key: "month",
    label: "Month",
  },
  {
    key: "repayment_no_overpayment",
    label: "Repayment (No Overpayment) (£)",
  },
  {
    key: "repayment_overpayment",
    label: "Repayment (Overpayment) (£)",
  },
  {
    key: "difference",
    label: "Difference (£)",
  },
  {
    key: "difference_discounted",
    label: "Difference Discounted to Present Day",
  },
  {
    key: "calculation",
    label: "Show Discounted Difference Calculation",
  },
];

const formatCurrency = (value) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const roundAndNormalizeNegativeZero = (value, digits = 8) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return numericValue;
  }

  const roundedValue = Number(numericValue.toFixed(digits));
  return Object.is(roundedValue, -0) ? 0 : roundedValue;
};

const formatPercentage = (value, digits = 1) => {
  const normalizedPercent = roundAndNormalizeNegativeZero(Number(value) * 100, digits);
  return `${normalizedPercent.toFixed(digits)}%`;
};

const formatDecimal = (value, digits = 8) =>
  roundAndNormalizeNegativeZero(value, digits).toFixed(digits);

const padEquation = (label, expression) => `${label.padEnd(45, " ")}= ${expression}`;

const getMonthlyDiscountRateFromAnnual = (annualRate) => {
  if (typeof annualRate !== "number" || !Number.isFinite(annualRate) || annualRate <= -1) {
    return null;
  }

  return (1 + annualRate) ** (1 / 12) - 1;
};

const getDifferenceDiscountedToPresentDay = (difference, monthlyRate, monthsInFuture) => {
  if (typeof monthlyRate !== "number" || !Number.isFinite(monthlyRate)) {
    return null;
  }

  return difference / ((1 + monthlyRate) ** monthsInFuture);
};

const formatCurrencyOrNotApplicable = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Not Applicable";
  }
  return formatCurrency(value);
};

const getRoundedDisplayedCurrencyValue = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number(numericValue.toFixed(2));
};

function getIrrDisplayColor(irr) {
  if (irr <= 0) return "#b42318";
  if (irr >= 0.06) return "#067647";

  const hue = (irr / 0.06) * 120;
  return `hsl(${hue}, 78%, 40%)`;
}

function buildDiscountedDifferenceExplanation(row, irr) {
  const annualDiscountFactor =
    typeof row.irr_annual === "number" && Number.isFinite(row.irr_annual)
      ? row.irr_annual
      : irr;
  const discountRateMonthly = getMonthlyDiscountRateFromAnnual(annualDiscountFactor);
  const isIrrApplicable =
    typeof annualDiscountFactor === "number" && Number.isFinite(annualDiscountFactor) &&
    typeof discountRateMonthly === "number" && Number.isFinite(discountRateMonthly);

  if (!isIrrApplicable) {
    return [
      "A value called the discount factor is used to work out how much future payments are worth in the present day.",
      "The IRR is the discount factor that gives a value of zero for the sum of all the Difference Discounted to Present Day.",
      "",
      "IRR is not applicable for this scenario because no single discount factor makes the discounted sum equal to zero.",
      "In this case, there is no valid discounting calculation to show for this row.",
    ].join("\n");
  }

  const isNegativeIrr = annualDiscountFactor < 0;

  const differenceDiscountedRaw = getDifferenceDiscountedToPresentDay(
    Number(row.difference),
    discountRateMonthly,
    Number(row.months_in_future)
  );
  const discountFactorPercent = formatDecimal(annualDiscountFactor * 100, 1);
  const discountFactor = formatDecimal(annualDiscountFactor, 8);
  const discountRateMonthlyDisplay = formatDecimal(discountRateMonthly, 8);
  const difference = formatDecimal(row.difference, 2);
  const differenceDiscounted = formatDecimal(differenceDiscountedRaw, 2);

  return [
    "A value called the discount factor is used to work out how much future payments are worth in the present day.",
    "The IRR is the discount factor that gives a value of zero for the sum of all the Difference Discounted to Present Day.",
    "Note the shown total for this column may be slightly above or below zero due to rounding each month's value to two decimal places before summing.",
    ...(isNegativeIrr
      ? [
          "",
          "A negative IRR is likely not a realistic return benchmark in practice, but this is the mathematically consistent discounting calculation.",
        ]
      : []),
    "",
    padEquation("Discount Factor Annual (IRR)", `${discountFactorPercent}%`),
    padEquation("", discountFactor),
    "",
    padEquation("Discount Factor Monthly", "(1 + Discount Factor Annual) ** (1 / 12) - 1"),
    padEquation("", `(1 + ${discountFactor}) ** (1 / 12) - 1`),
    padEquation("", discountRateMonthlyDisplay),
    "",
    padEquation(
      "Difference Discounted to Present Day",
      "Difference / (1 + Discount Factor Monthly) ** Months In Future"
    ),
    padEquation(
      "",
      `${difference} / (1 + ${discountRateMonthlyDisplay}) ** ${row.months_in_future}`
    ),
    padEquation("", differenceDiscounted),
  ].join("\n");
}

function getOverpaymentScenarioText(overpaymentType, overpaymentAmount, overpaymentLumpSumWholeThing) {
  if (overpaymentType === "lump_sum" && overpaymentLumpSumWholeThing) {
    return "Pay off entire loan this month";
  }

  const amount = Number(overpaymentAmount);
  if (!Number.isFinite(amount)) return "Not Provided";

  const formattedAmount = `£${formatCurrency(amount)}`;
  if (overpaymentType === "lump_sum") {
    return `${formattedAmount} lump sum this month`;
  }

  return `${formattedAmount} monthly`;
}

function OverpaymentComparisonTable({
  rows,
  irr,
  overpaymentType,
  overpaymentAmount,
  overpaymentLumpSumWholeThing,
}) {
  const [expandedCalculations, setExpandedCalculations] = useState({});
  const [isIrrCalculationVisible, setIsIrrCalculationVisible] = useState(false);
  const isIrrApplicable = typeof irr === "number" && Number.isFinite(irr);
  const showNegativeIrrWarning = !isIrrApplicable || irr < 0;
  const overpaymentScenarioText = getOverpaymentScenarioText(
    overpaymentType,
    overpaymentAmount,
    overpaymentLumpSumWholeThing,
  );

  if (!rows || rows.length === 0) return null;

  const renderHeaderCells = () => (
    HEADER_CONFIG.map((header) => (
      <th key={header.key}>
        {header.label}
        {header.key === "difference_discounted" ? (
          <span
            className="info-btn"
            tabIndex={0}
            aria-label="What is difference discounted to present day?"
          >
            <span className="info-icon">i</span>
            <span className="info-tooltip info-tooltip-below-left">
              £1 today is likely worth more than £1 in the future because the £1 today could be put into a savings account or invested to earn a return.
              <br />
              <br />
              A value called the discount factor is used to work out how much future payments are worth in the present day.
              <br />
              <br />
              The IRR is the discount factor that gives a value of zero for the sum of all the Difference Discounted to Present Day.
              <br />
              <br />
              Note the shown total for this column may be slightly above or below zero due to rounding each month's value to two decimal places before summing.
            </span>
          </span>
        ) : null}
      </th>
    ))
  );

  const totals = rows.reduce(
    (accumulator, row) => {
      const annualDiscountFactor =
        typeof row.irr_annual === "number" && Number.isFinite(row.irr_annual)
          ? row.irr_annual
          : irr;
      const monthlyDiscountRate = getMonthlyDiscountRateFromAnnual(annualDiscountFactor);
      const differenceDiscounted = getDifferenceDiscountedToPresentDay(
        Number(row.difference),
        monthlyDiscountRate,
        Number(row.months_in_future)
      );
      const roundedRepaymentNoOverpayment = getRoundedDisplayedCurrencyValue(
        row.repayment_no_overpayment
      );
      const roundedRepaymentOverpayment = getRoundedDisplayedCurrencyValue(
        row.repayment_overpayment
      );
      const roundedDifference = getRoundedDisplayedCurrencyValue(row.difference);
      const roundedDifferenceDiscounted = getRoundedDisplayedCurrencyValue(differenceDiscounted);

      return {
        repaymentNoOverpayment:
          accumulator.repaymentNoOverpayment + roundedRepaymentNoOverpayment,
        repaymentOverpayment:
          accumulator.repaymentOverpayment + roundedRepaymentOverpayment,
        difference: accumulator.difference + roundedDifference,
        differenceDiscounted:
          typeof roundedDifferenceDiscounted === "number" && Number.isFinite(roundedDifferenceDiscounted)
            ? accumulator.differenceDiscounted + roundedDifferenceDiscounted
            : accumulator.differenceDiscounted,
        hasInvalidDifferenceDiscounted:
          accumulator.hasInvalidDifferenceDiscounted ||
          !(typeof roundedDifferenceDiscounted === "number" && Number.isFinite(roundedDifferenceDiscounted)),
      };
    },
    {
      repaymentNoOverpayment: 0,
      repaymentOverpayment: 0,
      difference: 0,
      differenceDiscounted: 0,
      hasInvalidDifferenceDiscounted: false,
    }
  );

  return (
    <section className="overpayment-comparison-details" aria-label="Overpayment comparison">
      <h3 className="overpayment-comparison-heading">Overpayment Comparison - Important!</h3>
      <div className="overpayment-comparison-content">
        <div className="overpayment-table-footnote">
          <div className="overpayment-scenario-card">
            <span className="overpayment-metric-label">Overpayment Scenario</span>
            <strong className="overpayment-metric-value">{overpaymentScenarioText}</strong>
          </div>
          <div className="overpayment-metric-grid single-card-grid">
            <div className="overpayment-metric-card">
              <span className="overpayment-metric-label">IRR for Scenario (given other inputs)</span>
              <strong
                className="overpayment-metric-value"
                style={{ color: isIrrApplicable ? getIrrDisplayColor(irr) : "#b42318" }}
              >
                {isIrrApplicable ? formatPercentage(irr) : "Not Applicable (-100%)"}
              </strong>
            </div>
          </div>
          <p className="overpayment-footnote-copy">
            If you believe you could get a better annual return than the IRR (Internal Rate of Return) elsewhere, for example from a savings
            account or investing, it may make sense to do that rather than overpay your student loan. If your expected return
            elsewhere is lower than the IRR, overpaying your student loan may make sense.
          </p>
          <details className="irr-calculation-dropdown">
            <summary>What is IRR and why use it?</summary>
            <p className="overpayment-footnote-copy">
              The IRR can be thought of as an estimate of the boundary between whether overpaying is financially advantageous or not.
            </p>
            <p className="overpayment-footnote-copy">
              IRR is a more useful comparison than the total repayment difference between overpaying and not overpaying because it accounts for the time value of money:
              the fact that £1 today is likely worth more than £1 in the future because the £1 today could be put into a savings account or invested to earn a return.
            </p>
            <p className="overpayment-footnote-copy">
              IRR is also more useful than just looking at the interest rate of the loan. Whilst overpayment scenarios for high current incomes will have an IRR similar
              to the interest rate, for lower current incomes the IRR can be much lower than the interest rate because the loan balance is written off after a certain number of years.
            </p>
          </details>
          {showNegativeIrrWarning ? (
            <p className="overpayment-footnote-note">
              The reason people tend to overpay is in order to decrease the interest and so total repayment paid towards the loan.<br></br> 
              However a negative IRR means that with the overpayment scenario you end up paying more towards the student
              loan than if you had not overpaid. Therefore even a return elsewhere of 0 (stuffing your money
              under a mattress) is more financially advantageous than the overpayment scenario under the model inputs and assumptions.
            </p>
          ) : null}
          {!isIrrApplicable ? (
            <p className="overpayment-footnote-note">
              The IRR is Not Applicable if either:
              <br />- Under the overpayment scenario, you do not fully pay off your loan.
              <br />- Your loan is fully paid off in the first month without overpayment. This requires a huge income or a small current balance.
              <br />In these cases, the IRR for overpaying cannot be calculated and can be thought of as -100%.
            </p>
          ) : null}
          <div className="irr-calculation-dropdown">
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span className="irr-calculation-label">IRR calculation</span>
              <button
                type="button"
                className="expand-btn"
                onClick={() => setIsIrrCalculationVisible((prev) => !prev)}
              >
                {isIrrCalculationVisible ? "Hide" : "Explain"}
              </button>
            </div>
          </div>
          {isIrrCalculationVisible ? (
            <div className="overpayment-table-scroll">
              <div className="overpayment-table-sticky-header">
                <table className="styled-table overpayment-comparison-table overpayment-comparison-table-header">
                  <thead>
                    <tr>{renderHeaderCells()}</tr>
                  </thead>
                </table>
              </div>
              <table className="styled-table overpayment-comparison-table overpayment-comparison-table-body">
                <tbody>
                  {rows.map((row, idx) => {
                    const annualDiscountFactor =
                      typeof row.irr_annual === "number" && Number.isFinite(row.irr_annual)
                        ? row.irr_annual
                        : irr;
                    const monthlyDiscountRate = getMonthlyDiscountRateFromAnnual(annualDiscountFactor);
                    const differenceDiscounted = getDifferenceDiscountedToPresentDay(
                      Number(row.difference),
                      monthlyDiscountRate,
                      Number(row.months_in_future)
                    );
                    const rowKey = `${row.month}-${idx}`;
                    const isExpanded = Boolean(expandedCalculations[rowKey]);
                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <td>{row.month}</td>
                          <td>{formatCurrency(row.repayment_no_overpayment)}</td>
                          <td>{formatCurrency(row.repayment_overpayment)}</td>
                          <td>{formatCurrency(row.difference)}</td>
                          <td>{formatCurrencyOrNotApplicable(differenceDiscounted)}</td>
                          <td>
                            <button
                              className="expand-btn"
                              onClick={() =>
                                setExpandedCalculations((prev) => ({
                                  ...prev,
                                  [rowKey]: !prev[rowKey],
                                }))
                              }
                            >
                              {isExpanded ? "Hide" : "Explain"}
                            </button>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr className="overpayment-explanation-row">
                            <td colSpan={6} className="overpayment-explanation-cell">
                              <ExplanationBlock text={buildDiscountedDifferenceExplanation(row, irr)} />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div className="overpayment-total-bar" role="status" aria-live="polite">
                <span className="overpayment-total-label">Totals</span>
                <strong className="overpayment-total-cell">
                  {formatCurrency(totals.repaymentNoOverpayment)}
                </strong>
                <strong className="overpayment-total-cell">
                  {formatCurrency(totals.repaymentOverpayment)}
                </strong>
                <strong className="overpayment-total-cell">{formatCurrency(totals.difference)}</strong>
                <strong className="overpayment-total-cell">
                  {totals.hasInvalidDifferenceDiscounted
                    ? "Not Applicable"
                    : formatCurrency(totals.differenceDiscounted)}
                </strong>
                <span className="overpayment-total-empty" aria-hidden="true" />
              </div>
            </div>
          ) : null}
          <p className="overpayment-footnote-copy">
            Overpaying decisions depend on your personal circumstances, risk tolerance, and other factors.
            Please treat this output, including the IRR colour indicator, as a guide only.
            Student loan policy, inflation, interest rates, and your circumstances can all change over time,
            and this model does not include every factor (for example moving abroad, maternity leave,
            or tax implications).
          </p>
          <div className="overpayment-scenario-card overpayment-advice-card" role="note" aria-label="Important financial advice note">
            <p className="overpayment-footnote-copy overpayment-advice-copy">
              This is an illustrative estimate based only on the figures you entered and is not personalised
              financial advice. Please speak to a qualified financial adviser before making any overpayment decision.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OverpaymentComparisonTable;
